import { createContext, useContext, useEffect, useState, ReactNode } from "react";

const STORAGE_KEY = "lovable.viewAsUser";

interface ImpersonationContextValue {
  viewAsUser: boolean;
  setViewAsUser: (v: boolean) => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  viewAsUser: false,
  setViewAsUser: () => {},
});

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [viewAsUser, setViewAsUserState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setViewAsUser = (v: boolean) => {
    setViewAsUserState(v);
    try {
      if (v) localStorage.setItem(STORAGE_KEY, "1");
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
    // Sync across tabs
    window.dispatchEvent(new Event("lovable:impersonation-change"));
  };

  // Listen to changes from other tabs / events
  useEffect(() => {
    const onChange = () => {
      try {
        setViewAsUserState(localStorage.getItem(STORAGE_KEY) === "1");
      } catch {}
    };
    window.addEventListener("storage", onChange);
    window.addEventListener("lovable:impersonation-change", onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("lovable:impersonation-change", onChange);
    };
  }, []);

  return (
    <ImpersonationContext.Provider value={{ viewAsUser, setViewAsUser }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
